/**
 * Pushes the GA4 storefront e-commerce events onto the dataLayer for GTM:
 * view_item_list, view_item and add_to_cart.
 *
 * These used to come from the Shopify Google & YouTube app. Disconnecting its
 * GA4 tracking on switch-over day took them with it, so the theme has to send
 * them itself. The five Hydrogen storefronts do the same job in
 * app/components/Analytics/GtmEcommerceEvents, and this has to agree with it.
 *
 * Purchase is deliberately absent. It is sent server-side from the order
 * webhook for all six sites, and a second one from here would be dropped by
 * GA4 anyway, since it keeps one event per transaction_id.
 *
 * Every push clears `ecommerce` first. GTM reads the dataLayer as an
 * accumulating object, so without the clear, items from one event leak into
 * the next and products appear in lists they were never shown in.
 *
 * `site_id` goes on every push. All six sites report into one GA4 property,
 * so it is the only thing separating the brands, and it must match the
 * `brand_source` the cart carries exactly, case included.
 */
(function () {
	'use strict'

	var config = window.LTAGa4 || {}
	var SITE_ID = config.siteId || ''
	var CURRENCY = config.currency || 'USD'

	window.dataLayer = window.dataLayer || []

	if (!SITE_ID) {
		console.error('a-ga4-ecommerce: `siteId` is not set, events would carry no brand. Nothing sent.')
		return
	}

	/** Liquid and the Ajax cart both deal in cents. GA4 wants units. */
	function toPrice(cents) {
		var value = Number(cents)
		return Number.isFinite(value) ? Math.round(value) / 100 : 0
	}

	function sumValue(items) {
		return (
			Math.round(
				items.reduce(function (total, item) {
					return total + item.price * (item.quantity || 1)
				}, 0) * 100
			) / 100
		)
	}

	function push(event, ecommerce) {
		window.dataLayer.push({ ecommerce: null })
		window.dataLayer.push({ event: event, site_id: SITE_ID, ecommerce: ecommerce })
	}

	/*****************************************
	 * view_item_list
	 ******************************************/

	/** Cards declare themselves with a-ga4-item.liquid; lists just wrap them. */
	function readItems(container) {
		var items = []
		var cards = container.querySelectorAll('[data-ga4-item]')

		for (var i = 0; i < cards.length; i += 1) {
			try {
				var item = JSON.parse(cards[i].getAttribute('data-ga4-item'))
				items.push({
					item_id: item.item_id,
					item_name: item.item_name,
					price: toPrice(item.price),
				})
			} catch (error) {
				// One malformed card should not cost the whole list.
			}
		}

		return items
	}

	function pushItemList(container) {
		var items = readItems(container)

		// An empty collection page, or a slider whose section has no products
		// chosen yet. GA4 rejects a list with no items, so there is nothing
		// useful to report.
		if (!items.length) return

		push('view_item_list', {
			item_list_id: container.getAttribute('data-ga4-list-id') || '',
			item_list_name: container.getAttribute('data-ga4-list-name') || '',
			items: items,
		})
	}

	/**
	 * Lists arrive three ways: in the page, injected later (the cart drawer
	 * upsell re-renders on every cart change), and revealed by a tab switch.
	 *
	 * The first two are each a first appearance and push once, tracked on the
	 * element itself so a re-render of the same drawer does not double count.
	 * A tab switch is a deliberate act of looking at a different set, so it
	 * pushes again even for a panel already seen.
	 */
	function pushOnce(container) {
		if (container.ltaGa4Listed) return
		container.ltaGa4Listed = true
		pushItemList(container)
	}

	function scan(root) {
		if (root.nodeType !== 1) return
		if (root.hasAttribute('data-ga4-item-list') && isVisible(root)) pushOnce(root)

		var lists = root.querySelectorAll('[data-ga4-item-list]')
		for (var i = 0; i < lists.length; i += 1) {
			if (isVisible(lists[i])) pushOnce(lists[i])
		}
	}

	/**
	 * Only inactive tab panels hide a list that is otherwise in the page, and
	 * both tabbed sections mark the live one with `data-active`. Checking the
	 * panel rather than measuring the element keeps this independent of how
	 * the tabs animate, and means a list below the fold still counts as shown,
	 * which is what the Google & YouTube app did.
	 */
	var PANEL_SELECTOR = '[data-fptab-products], [data-prec-products]'

	function isVisible(container) {
		var panel = container.closest(PANEL_SELECTOR)
		return !panel || panel.getAttribute('data-active') === 'true'
	}

	function watchForLists() {
		new MutationObserver(function (mutations) {
			for (var i = 0; i < mutations.length; i += 1) {
				var mutation = mutations[i]

				if (mutation.type === 'attributes') {
					var panel = mutation.target
					if (panel.getAttribute('data-active') !== 'true') continue

					// The tabs carry the list on the panel itself, so the panel
					// can be the list rather than only containing one.
					if (panel.hasAttribute('data-ga4-item-list')) pushItemList(panel)

					var panelLists = panel.querySelectorAll('[data-ga4-item-list]')
					for (var j = 0; j < panelLists.length; j += 1) {
						pushItemList(panelLists[j])
					}
					continue
				}

				for (var k = 0; k < mutation.addedNodes.length; k += 1) {
					scan(mutation.addedNodes[k])
				}
			}
		}).observe(document.documentElement, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['data-active'],
		})
	}

	/*****************************************
	 * view_item
	 ******************************************/

	/**
	 * The product page carries the title, and the variant picker already
	 * renders the selected variant as JSON for the theme's own use. Reading
	 * that rather than rendering a second copy means the two can never
	 * disagree about which variant is selected.
	 */
	function pushViewItem(variant) {
		var marker = document.querySelector('[data-ga4-view-item]')
		if (!marker || !variant) return

		var sku = (variant.sku || '').trim()
		var price = toPrice(variant.price)

		push('view_item', {
			currency: CURRENCY,
			value: price,
			items: [
				{
					item_id: sku || String(variant.id),
					item_name: marker.getAttribute('data-ga4-product-title') || '',
					price: price,
					quantity: 1,
				},
			],
		})
	}

	/**
	 * The variant picker block renders the selected variant as JSON for the
	 * theme's own use, so read that rather than a second copy that could
	 * disagree with it.
	 *
	 * It falls back to the product section's own data because that block can
	 * be removed from the template in the editor. Without the fallback, doing
	 * so would stop view_item firing site-wide with nothing to show for it.
	 */
	function readSelectedVariant(marker) {
		var node = document.querySelector('[data-selected-variant]')

		if (node) {
			try {
				return JSON.parse(node.textContent)
			} catch (error) {
				// Fall through to the marker.
			}
		}

		try {
			var item = JSON.parse(marker.getAttribute('data-ga4-item'))
			return { id: item.item_id, sku: item.item_id, price: item.price }
		} catch (error) {
			return null
		}
	}

	function watchProduct() {
		var marker = document.querySelector('[data-ga4-view-item]')
		if (!marker) return

		pushViewItem(readSelectedVariant(marker))

		// Switching variant is a new product as far as GA4 is concerned, so it
		// gets its own view_item. A variant combination that does not exist
		// arrives here as null and is skipped.
		document.addEventListener('l:variant:changed', function (event) {
			pushViewItem(event.detail && event.detail.variant)
		})
	}

	/*****************************************
	 * add_to_cart
	 ******************************************/

	/**
	 * Quantity comes from what was requested, not from the response.
	 *
	 * The Ajax cart answers with the line's *new* total, so adding one bottle
	 * to a cart that already held two reports three. GA4 would then show more
	 * added than ever was. a-global.js passes the requested quantities through
	 * on the event for exactly this reason.
	 */
	function requestedQuantity(requested, line) {
		for (var i = 0; i < requested.length; i += 1) {
			if (String(requested[i].id) === String(line.id)) return requested[i].quantity
		}
		return line.quantity || 1
	}

	function watchAddToCart() {
		// Fires only once the cart has actually accepted the item, so a failed
		// add reports nothing. One listener covers every place a product can
		// be added, since they all go through the same cart service.
		document.addEventListener('l:product:added', function (event) {
			var detail = event.detail || {}
			var response = detail.response
			if (!response) return

			var lines = response.items || [response]
			var requested = detail.requested || []

			var items = []
			for (var i = 0; i < lines.length; i += 1) {
				var line = lines[i]
				if (!line || !line.id) continue

				var sku = (line.sku || '').trim()
				items.push({
					item_id: sku || String(line.id),
					item_name: line.product_title || line.title || '',
					price: toPrice(line.price),
					quantity: requestedQuantity(requested, line),
				})
			}

			if (!items.length) return

			push('add_to_cart', {
				currency: CURRENCY,
				value: sumValue(items),
				items: items,
			})
		})
	}

	/*****************************************
	 * Init
	 ******************************************/

	function init() {
		// Observe before the first scan, so a list injected while that scan
		// runs is still seen. pushOnce makes the overlap harmless.
		watchForLists()
		scan(document.documentElement)
		watchProduct()
		watchAddToCart()
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init)
	} else {
		init()
	}
})()
